import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Alert } from "react-bootstrap";
import api from "services/api";
import emailValidate from "services/emailValidate";
import Translator, { __ } from "services/Translator";
import ModalCloseButton from "components/ModalCloseButton";
import SignupBonusDropdown from "components/SignupBonusDropdown";
import SocialLogin from "components/SocialLogin";
import { AuthBottomWelcomePromo, AuthMobileWelcomeOffer } from "components/AuthWelcomePromo";
import { sanitizeTextInput } from "utils/profileValidation";
import { buildLoginOtpPayload, requestLoginOtp } from "services/authOtp";
import { isPWA } from "utils/isPWA";

export default function StreamlinedAuthModalBody({
  alertMsg,
  closeLoginModal,
  props,
  loginForm,
  errors,
  updateInput,
  checkInput,
  setSpinner,
  referralCode,
  setReferralCode,
  showReferralField,
  setShowReferralField,
  onOtpRequested,
}) {
  const selectedLanguage = Translator.getSelectedLanguage();
  // In the installed PWA the auth screen is a focused, full-screen login/register
  // gate, so the welcome-offer promo panels are omitted (no banners, nothing to
  // scroll past). The browser keeps them unchanged.
  const inPwa = isPWA();
  const [campaigns, setCampaigns] = useState([]);
  const [isCampaignsLoading, setIsCampaignsLoading] = useState(true);
  const [bonusOffer, setBonusOffer] = useState("no_bonus");
  const [hasManualBonusSelection, setHasManualBonusSelection] = useState(false);
  const [stepOneError, setStepOneError] = useState("");
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [ageConfirmation, setAgeConfirmation] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [consentErrors, setConsentErrors] = useState({});

  const getCampaigns = async () => {
    setIsCampaignsLoading(true);
    try {
      const response = await api.post("/get-registration-campaigns", {
        language: selectedLanguage?.code,
        currency: "USD",
      });
      if (response.status === 200) {
        setCampaigns(Array.isArray(response.data) ? response.data : []);
      }
    } finally {
      setIsCampaignsLoading(false);
    }
  };

  useEffect(() => {
    getCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage?.code]);

  useEffect(() => {
    if (hasManualBonusSelection) return;
    if (!campaigns.length || isCampaignsLoading) return;
    setBonusOffer(campaigns[0].id);
  }, [campaigns, hasManualBonusSelection, isCampaignsLoading]);

  useEffect(() => {
    if (props.referralCode) {
      setReferralCode(props.referralCode);
      setShowReferralField(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.referralCode]);

  const handleBonusSelection = (value) => {
    setBonusOffer(value);
    setHasManualBonusSelection(true);
  };

  const handleEmailChange = (ev) => {
    setStepOneError("");
    updateInput(ev);
  };

  const handleStepOneSubmit = async (ev) => {
    ev.preventDefault();
    const email = sanitizeTextInput(loginForm.email, 250).toLowerCase();

    if (!emailValidate(email)) {
      setStepOneError(__("Please enter a valid email address"));
      return;
    }

    const nextConsentErrors = {};
    if (!ageConfirmation) {
      nextConsentErrors.ageConfirmation = __("Please check the age confirmation.");
    }
    if (!agreeToTerms) {
      nextConsentErrors.agreeToTerms = __("Please check the terms and conditions.");
    }
    setConsentErrors(nextConsentErrors);
    if (Object.keys(nextConsentErrors).length) {
      return;
    }

    setIsSendingOtp(true);
    setSpinner(true);
    setStepOneError("");

    try {
      const payload = buildLoginOtpPayload({
        email,
        bonusOffer,
        referralCode: referralCode || props.referralCode,
        language: selectedLanguage?.code,
        ageConfirmation,
        agreeToTerms,
      });
      const response = await requestLoginOtp(payload);

      if (response.status === 200) {
        onOtpRequested?.({
          email,
          bonusOffer,
          referralCode: referralCode || props.referralCode || "",
        });
      } else {
        setStepOneError(response.data?.message || __("Unable to send verification code"));
      }
    } catch (err) {
      setStepOneError(err?.message || __("Unable to send verification code"));
    } finally {
      setIsSendingOtp(false);
      setSpinner(false);
    }
  };

  return (
    <div className="betmode-auth">
      <Alert show={alertMsg.showAlert} variant={alertMsg.variant}>
        {alertMsg.message}
      </Alert>

      <section className="betmode-auth__panel betmode-auth__panel--form">
        <ModalCloseButton onClick={closeLoginModal} className="betmode-auth__close modal_close_reg" />

        <div className="betmode-auth__brand">
          <img
            alt="Play365"
            className="betmode-auth__logo"
            src={require("assets/play365/images/logos/logo.webp")}
          />
        </div>

        <h2 className="betmode-auth__title">{__("Login or Register")}</h2>

        <form className="betmode-auth__form" autoComplete="off" onSubmit={handleStepOneSubmit}>
          <div className="betmode-auth__field">
            <label className="betmode-auth__label" htmlFor="betmode-auth-email">
              {__("Your email")}
            </label>
            <input
              id="betmode-auth-email"
              type="email"
              name="email"
              value={loginForm.email}
              onChange={handleEmailChange}
              onBlur={checkInput}
              autoComplete="email"
              className={
                "betmode-auth__input " + (errors.email || stepOneError ? "betmode-auth__input--error" : "")
              }
              placeholder={__("Email")}
            />
            <div className="betmode-auth__error">{stepOneError || errors.email}</div>
          </div>

          <div className="betmode-auth__field betmode-auth__field--bonus">
            <SignupBonusDropdown
              campaigns={campaigns}
              isCampaignsLoading={isCampaignsLoading}
              bonusOffer={bonusOffer}
              onSelectBonus={handleBonusSelection}
            />
          </div>

          {!showReferralField ? (
            <button
              type="button"
              className="betmode-auth__referral-link"
              onClick={() => setShowReferralField(true)}
            >
              {__("Have referral code? Click here!")}
            </button>
          ) : (
            <div className="betmode-auth__field">
              <input
                type="text"
                name="referralCode"
                value={referralCode}
                onChange={(ev) => setReferralCode(ev.target.value)}
                autoComplete="off"
                className="betmode-auth__input"
                placeholder={__("Referral code")}
              />
            </div>
          )}

          <div className="betmode-auth__consent">
            <div className="reg_check_area">
              <input
                type="checkbox"
                className="stepTerms"
                id="streamlined-age-confirmation"
                name="ageConfirmation"
                checked={ageConfirmation}
                onChange={(ev) => {
                  setAgeConfirmation(ev.target.checked);
                  setConsentErrors((prev) => ({ ...prev, ageConfirmation: "" }));
                }}
              />
              <label htmlFor="streamlined-age-confirmation" className="check_area">
                <div className="check_check_and_text_area">
                  <div className="check_box">
                    <div className="check_checked_box"></div>
                  </div>
                  <div className="check_text">
                    {__(
                      "I declare that I am 18 years or older, located in a permitted territory and have no self-exclusions."
                    )}
                    <span className="requried_icon">*</span>
                  </div>
                </div>
              </label>
              <div className="errorMsg">{consentErrors.ageConfirmation}</div>
            </div>

            <div className="reg_check_area">
              <input
                type="checkbox"
                className="stepTerms"
                id="streamlined-agree-terms"
                name="agreeToTerms"
                checked={agreeToTerms}
                onChange={(ev) => {
                  setAgreeToTerms(ev.target.checked);
                  setConsentErrors((prev) => ({ ...prev, agreeToTerms: "" }));
                }}
              />
              <label htmlFor="streamlined-agree-terms" className="check_area">
                <div className="check_check_and_text_area">
                  <div className="check_box">
                    <div className="check_checked_box"></div>
                  </div>
                  <div className="check_text">
                    {__("I have read and accept the") + " "}
                    <Link
                      hreflang={selectedLanguage?.code}
                      rel="nofollow noopener noreferrer"
                      to="/terms-and-conditions"
                      onClick={closeLoginModal}
                    >
                      {__("Terms of service")}
                    </Link>
                    ,{" "}
                    <Link
                      hreflang={selectedLanguage?.code}
                      rel="nofollow noopener noreferrer"
                      to="/privacy-policy"
                      onClick={closeLoginModal}
                    >
                      {__("Privacy policy")}
                    </Link>{" "}
                    {__("and")}{" "}
                    <Link
                      hreflang={selectedLanguage?.code}
                      rel="nofollow noopener noreferrer"
                      to="/responsible-gaming"
                      onClick={closeLoginModal}
                    >
                      {__("Responsible gaming policy")}
                    </Link>
                    .<span className="requried_icon">*</span>
                  </div>
                </div>
              </label>
              <div className="errorMsg">{consentErrors.agreeToTerms}</div>
            </div>
          </div>

          <button type="submit" className="betmode-auth__submit" disabled={isSendingOtp}>
            {isSendingOtp ? __("Sending code…") : __("Continue")}
          </button>
          <p className="betmode-auth__submit-hint">
            {__("We'll email you a 6-digit code to finish — no password needed.")}
          </p>
        </form>
      </section>

      <section className="betmode-auth__panel betmode-auth__panel--social">
        <p className="betmode-auth__social-heading">{__("Or register with:")}</p>
        <SocialLogin
          callFrom="modal"
          variant="betmode"
          googleButtonId="googleLoginStreamlined"
          bonusOffer={bonusOffer}
          referralCode={referralCode || props.referralCode}
          showOrDivider={false}
          {...props}
        />
      </section>

      {!inPwa && props.isMobileDevice ? (
        <section className="betmode-auth__panel betmode-auth__panel--mobile-offer">
          <AuthMobileWelcomeOffer />
        </section>
      ) : null}

      {!inPwa ? (
        <section className="betmode-auth__panel betmode-auth__panel--promo">
          <AuthBottomWelcomePromo variant="betmode" />
        </section>
      ) : null}
    </div>
  );
}
